import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { usePerm } from './hooks/usePerm';
import { usePush, pushErrorKey } from './hooks/usePush';
import { InstallPromptProvider, useInstallPromptState } from './hooks/useInstallPrompt';
import { registerServiceWorker } from './utils/serviceWorker';
// Критический путь первого запуска — eager, в main-бандле:
// Layout + дашборды (авторизованный PWA-запуск) и LoginPage (первый визит).
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardInsights from './pages/DashboardInsights';
import WorkerDashboard from './pages/WorkerDashboard';
import { useKeyboardOffset } from './hooks/useKeyboardOffset';
import { track } from './analytics';
import PageviewTracker from './analytics/PageviewTracker';
import AnalyticsConsentBanner from './components/AnalyticsConsentBanner';
import { BASE_DOMAIN, SUBSCRIPTION_TAB_ENABLED } from './branding';
import { redirectToCanonicalHost } from './utils/sessionHandoff';
import './index.css';

/**
 * lazyPage — React.lazy с защитой от исчезнувших чанков.
 * После деплоя хэши чанков меняются; открытая старая вкладка при переходе
 * на страницу получит 404 → ChunkLoadError. В этом случае один раз
 * перезагружаем страницу (свежий index.html подтянет новые чанки).
 * Флаг в sessionStorage защищает от бесконечного цикла reload.
 */
function lazyPage(importer) {
  return lazy(() =>
    importer()
      .then((m) => { sessionStorage.removeItem('pm_chunk_reload'); return m; })
      .catch((err) => {
        if (!sessionStorage.getItem('pm_chunk_reload')) {
          sessionStorage.setItem('pm_chunk_reload', '1');
          window.location.reload();
        }
        throw err;
      })
  );
}

// Остальные страницы — отдельные чанки, грузятся при первом переходе.
const ForgotPasswordPage   = lazyPage(() => import('./pages/ForgotPasswordPage'));
const RegisterPage         = lazyPage(() => import('./pages/RegisterPage'));
const OnboardingSetupPage  = lazyPage(() => import('./pages/OnboardingSetupPage'));
const OnboardingDomainPage = lazyPage(() => import('./pages/OnboardingDomainPage'));
const ClientsPage          = lazyPage(() => import('./pages/ClientsPage'));
const ClientDetailPage     = lazyPage(() => import('./pages/ClientDetailPage'));
const PropertiesPage       = lazyPage(() => import('./pages/PropertiesPage'));
const PropertyDetailPage   = lazyPage(() => import('./pages/PropertyDetailPage'));
const TasksPage            = lazyPage(() => import('./pages/TasksPage'));
const StatsPage            = lazyPage(() => import('./pages/StatsPage'));
const SettingsPage         = lazyPage(() => import('./pages/SettingsPage'));
const InvitePage           = lazyPage(() => import('./pages/InvitePage'));
const VaultInvitePage      = lazyPage(() => import('./pages/VaultInvitePage'));
const HelpPage             = lazyPage(() => import('./pages/HelpPage'));
const ProfilePage          = lazyPage(() => import('./pages/ProfilePage'));
const DemoPage             = lazyPage(() => import('./pages/DemoPage'));
const ProjectsPage         = lazyPage(() => import('./pages/ProjectsPage'));
const ProjectDetailPage    = lazyPage(() => import('./pages/ProjectDetailPage'));
const ExpensesPage         = lazyPage(() => import('./pages/ExpensesPage'));
const QuotesPage           = lazyPage(() => import('./pages/QuotesPage'));
const RequestsPage         = lazyPage(() => import('./pages/RequestsPage'));
// «Парк» — где сейчас вся переносная техника (equipment_mobility_plan.md, фаза 3).
const FleetPage            = lazyPage(() => import('./pages/FleetPage'));
const AmcPublicPage        = lazyPage(() => import('./pages/AmcPublicPage'));
const QuotePublicPage      = lazyPage(() => import('./pages/QuotePublicPage'));
const EquipmentEntryPage   = lazyPage(() => import('./pages/EquipmentEntryPage'));
const EquipmentCodePage    = lazyPage(() => import('./pages/EquipmentCodePage'));

// Фолбэк на время загрузки чанка — тот же спиннер, что и везде.
export function RouteLoading() {
  return (
    <div className="loading-screen">
      <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
    </div>
  );
}

// Канонический хост тенанта: если авторизованный человек открыл приложение не
// на своём адресе — базовый домен, app./my.<BASE_DOMAIN>, чужой поддомен —
// уводим его на верный, забрав сессию (redirectToCanonicalHost). Без этого
// строка адреса, фавикон и бренд в шапке остаются от базового домена, хотя
// показывается кабинет конкретного тенанта.
//
// ⚠️ onOwnInfra — обязательная проверка ДО сравнения хостов: без неё в dev
// (hostname=localhost) любая орга с непустым subdomain улетала бы редиректом
// на несуществующий localhost.<BASE_DOMAIN>. Ограничиваем механизм только
// хостами нашей инфры (BASE_DOMAIN и его поддомены) — custom_domain тенанта
// тоже туда не входит, что и требуется: если человек уже на custom_domain,
// сравнение просто не запускается.
function CanonicalHostGate({ user, children }) {
  const hostname = window.location.hostname;
  const onOwnInfra = hostname === BASE_DOMAIN || hostname.endsWith(`.${BASE_DOMAIN}`);
  const canonicalHost = (!user.is_demo && onOwnInfra)
    ? (user.custom_domain || (user.subdomain ? `${user.subdomain}.${BASE_DOMAIN}` : null))
    : null;
  const mismatch = !!canonicalHost && hostname !== canonicalHost;
  const { t } = useTranslation();
  // Переход «завис»: страница через 3 с всё ещё здесь. Типичный случай —
  // Android: у человека установлено приложение с поддомена тенанта, и ссылку на
  // этот поддомен система открывает в приложении, а вкладка (браузер внутри
  // Telegram и т.п.) остаётся на app. с вечной крутилкой. Сессия при этом уже
  // переехала — просто показываем ссылку, по которой можно уйти руками.
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!mismatch) return undefined;
    redirectToCanonicalHost(canonicalHost);
    const timer = setTimeout(() => {
      setStuck(true);
      track('canonical_host_redirect_stuck', { target_host: canonicalHost });
    }, 3000);
    return () => clearTimeout(timer);
  }, [mismatch, canonicalHost]);

  if (mismatch) {
    // Без кода переноса: он одноразовый и, скорее всего, уже потрачен. Токен
    // к этому моменту лежит на целевом origin (обмен прошёл), а если нет —
    // человек увидит там обычный вход, а не крутилку.
    const href = `${window.location.protocol}//${canonicalHost}${window.location.pathname}${window.location.search}`;
    return (
      <div className="loading-screen" data-ph-capture-attribute-component="canonical_host_redirect">
        <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
        {stuck && (
          <div style={{ textAlign: 'center', fontSize: 14, lineHeight: 1.5, padding: '0 16px' }}>
            <div>{t('login.canonicalRedirectStuck')}</div>
            <a
              href={href}
              style={{ color: 'var(--accent)', fontWeight: 600, wordBreak: 'break-all' }}
              data-ph-capture-attribute-action="open_canonical_host"
              onClick={() => track('canonical_host_redirect_manual', { target_host: canonicalHost })}
            >
              {t('login.canonicalRedirectOpen', { host: canonicalHost })}
            </a>
          </div>
        )}
      </div>
    );
  }
  return children;
}

function PrivateRoute({ children, perm, moduleFlag }) {
  const { user, loading } = useAuth();
  const { can } = usePerm();
  if (loading) return (
    <div className="loading-screen">
      <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
      <span>Загрузка...</span>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Демо-тенанты пропускают онбординг
  if (!user.is_demo) {
    // Шаг 1: выбор субдомена (теперь первый шаг)
    if (!user.subdomain) return <Navigate to="/onboarding/domain" replace />;
    // Шаг 2: название компании + логотип
    if (user.setup_completed === false) return <Navigate to="/onboarding/setup" replace />;
  }
  // Доступ к разделу — по праву роли (RBAC). Список ролей как проп больше не
  // поддерживается: все разделы переведены на права.
  //
  // Массив означает «хотя бы одно из». Нужен там, где страница собрана из
  // разнородных вкладок: в Настройках карточку организации открывает
  // org_settings, а печать QR-наклеек — equipment.print_tags, и по дефолтам
  // RBAC второе есть у Бригадира, у которого первого нет.
  const need = Array.isArray(perm) ? perm : (perm ? [perm] : []);
  if (need.length && !need.some((p) => can(p))) return <Navigate to="/" replace />;
  // Модуль выключен на уровне организации → закрываем прямой доступ по URL.
  if (moduleFlag && user[moduleFlag] === false) return <Navigate to="/" replace />;
  return <CanonicalHostGate user={user}>{children}</CanonicalHostGate>;
}

// Лендинг по роли: admin/superadmin → новый Дашборд, worker → Работы.
function IndexRedirect() {
  // Лендинг для всех ролей — Дашборд (admin/superadmin → DashboardInsights,
  // worker → WorkerDashboard; выбор внутри DashboardByRole).
  return <Navigate to="/dashboard" replace />;
}

// Раздел «Клиенты» переименован в «Контакты» и переехал на /contacts.
// Старый путь оставлен навсегда как редирект: диплинки /clients/:id уже уехали
// в пуш-уведомления (services/push.js) и в закладки тенантов — без него они
// открывают 404. Query и hash переносим, id — из параметра.
function LegacyClientsRedirect() {
  const { id } = useParams();
  const { search, hash } = useLocation();
  const to = `/contacts${id ? `/${id}` : ''}${search}${hash}`;

  // Разметка: видно, когда поток по старым ссылкам иссякнет и редирект можно
  // будет снимать. Один раз на монтирование, не на каждый ре-рендер.
  useEffect(() => {
    track('legacy_clients_url_redirected', { to_id: id ?? null });
  }, [id]);

  return <Navigate to={to} replace />;
}

// Дашборд по области данных: узкая (свои Работы ИЛИ своя команда) →
// WorkerDashboard, вся организация → DashboardInsights.
//
// ⚠️ Раньше здесь было isOwnScope, и Бригадир (область «своя команда») получал
// обзорный дашборд организации со сводкой по всем клиентам, объектам и деньгам.
function DashboardByRole() {
  const { isNarrowScope } = usePerm();
  return isNarrowScope ? <WorkerDashboard /> : <DashboardInsights />;
}

// Регистрация Service Worker — строго после логина, не на / login/register.
// SW делает сайт «устанавливаемым»; регистрировать его раньше, чем у тенанта
// появилось лого, значит предлагать «Добавить на экран» ставить нечего
// (см. utils/serviceWorker.js). Ref — чтобы не звать register() повторно на
// каждый ре-рендер AuthProvider.
function ServiceWorkerRegistrar() {
  const { user } = useAuth();
  const registeredRef = useRef(false);
  useEffect(() => {
    if (!user || registeredRef.current) return;
    registeredRef.current = true;
    registerServiceWorker();
  }, [user]);
  return null;
}

// Платформенный гард (PlatformRoute) удалён вместе с /billing: единственный
// кросс-тенантный экран в приложении переехал на stat.fixty.io. Признак
// is_platform_owner в /auth/me остаётся — на нём держится доступ к API.

// Промпт подписки на пуши для Админа/Суперадмина при первом входе
function PushOnboardingPrompt() {
  const { t: tr } = useTranslation();
  const { user } = useAuth();
  const { isSupported, isSubscribed, subscribe } = usePush();

  useEffect(() => {
    if (!user || !isSupported || isSubscribed) return;
    if (!user.setup_completed) return;
    if (!user.subdomain) return;
    // Промпт нужен тем, кто ведёт чужие Работы, а не только свои
    if (user.scope === 'own') return;

    const key = `pm_push_prompted_${user.id}`;
    if (localStorage.getItem(key)) return;

    // Показываем промпт с небольшой задержкой — после загрузки страницы
    const timer = setTimeout(() => {
      localStorage.setItem(key, '1');
      toast((t) => (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span>{tr('pushOnboarding.title')}</span>
          <span style={{ fontSize: 12, opacity: 0.75 }}>{tr('pushOnboarding.hint')}</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button
              data-ph-capture="push_onboarding_accept"
              style={{ padding: '4px 12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
              onClick={async () => {
                track('push_onboarding_accept');
                toast.dismiss(t.id);
                const result = await subscribe();
                if (result.ok) {
                  // ⚠️ Не полагаться на дефолтный автотаймер тоста: тап по кнопке
                  // внутри тоста синтезирует mouseenter на контейнере Toaster,
                  // ставя всю группу на паузу до mouseleave — которого на тач-
                  // устройствах не бывает. Без mouseleave таймер закрытия вообще
                  // не планируется (react-hot-toast), и тост «залипает» бессрочно,
                  // переживая переход на другие экраны. toast.dismiss() снимает
                  // тост напрямую, минуя внутренний таймер и паузу.
                  const id = toast.success(tr('pushOnboarding.enabled'));
                  setTimeout(() => toast.dismiss(id), 3000);
                } else {
                  // Любая причина, не только отказ в разрешении — раньше
                  // таймаут/сбой подписки проходили молча (см. usePush.js).
                  const errKey = pushErrorKey(result);
                  if (errKey) {
                    const id = toast.error(tr(errKey, { message: result.message || result.reason || '' }));
                    setTimeout(() => toast.dismiss(id), 6000);
                  }
                }
              }}
            >
              {tr('pushOnboarding.enable')}
            </button>
            <button
              data-ph-capture="push_onboarding_dismiss"
              style={{ padding: '4px 12px', background: 'transparent', border: '1px solid var(--border, #ddd)', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
              onClick={() => {
                track('push_onboarding_dismiss');
                toast.dismiss(t.id);
              }}
            >
              {tr('pushOnboarding.later')}
            </button>
          </span>
        </span>
      ), { duration: 15000, id: 'push-onboarding' });
    }, 2000);

    return () => clearTimeout(timer);
  }, [user, isSupported, isSubscribed, subscribe, tr]);

  return null;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Suspense fallback={<RouteLoading />}>
    <Routes>
      <Route path="/login" element={
        !user ? <LoginPage /> : <Navigate to="/" replace />
      } />
      <Route path="/register" element={
        !user ? <RegisterPage /> : <Navigate to="/" replace />
      } />
      <Route path="/forgot" element={
        !user ? <ForgotPasswordPage /> : <Navigate to="/" replace />
      } />
      <Route path="/demo" element={user ? <Navigate to="/" replace /> : <DemoPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/amc/:token" element={<AmcPublicPage />} />
      <Route path="/q/:token" element={<QuotePublicPage />} />
      {/* Вход по QR-наклейке оборудования. ⚠️ Маршрут ВНЕ PrivateRoute: страница
          сама решает, что делать с неавторизованным (сейчас — логин с
          возвратом, в Ф3 — публичный паспорт). Под PrivateRoute она потеряла бы
          адрес наклейки при редиректе, и человек у печи остался бы ни с чем. */}
      <Route path="/e/:token" element={<EquipmentEntryPage />} />
      {/* `/e` без кода — ровно тот адрес, что напечатан на наклейке в строке
          «No camera? Enter code at …». Публичный, как и сама наклейка. */}
      <Route path="/e" element={<EquipmentCodePage />} />
      <Route path="/vault-invite" element={<VaultInvitePage />} />
      {/* Онбординг шаг 1: выбор субдомена */}
      <Route
        path="/onboarding/domain"
        element={
          !user
            ? <Navigate to="/login" replace />
            : user.is_demo
              ? <Navigate to="/" replace />
              : user.subdomain
                ? <Navigate to="/" replace />
                : <OnboardingDomainPage />
        }
      />
      {/* Онбординг шаг 2: название + логотип */}
      <Route
        path="/onboarding/setup"
        element={
          !user
            ? <Navigate to="/login" replace />
            : user.is_demo
              ? <Navigate to="/" replace />
              : !user.subdomain
                ? <Navigate to="/onboarding/domain" replace />
                : user.setup_completed
                  ? <Navigate to="/" replace />
                  : <OnboardingSetupPage />
        }
      />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        {/* Лендинг по роли: admin/superadmin → Дашборд, worker → Работы */}
        <Route index element={<IndexRedirect />} />
        <Route path="dashboard" element={<PrivateRoute><DashboardByRole /></PrivateRoute>} />
        <Route path="contacts" element={<PrivateRoute perm="clients.view" moduleFlag="clients_enabled"><ClientsPage /></PrivateRoute>} />
        <Route path="contacts/:id" element={<PrivateRoute perm="clients.view" moduleFlag="clients_enabled"><ClientDetailPage /></PrivateRoute>} />
        {/* Legacy: старые ссылки на /clients — редирект, гейты отработают на /contacts */}
        <Route path="clients" element={<LegacyClientsRedirect />} />
        <Route path="clients/:id" element={<LegacyClientsRedirect />} />
        <Route path="properties" element={<PropertiesPage />} />
        <Route path="properties/:id" element={<PropertyDetailPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="projects" element={<PrivateRoute perm="projects.view"><ProjectsPage /></PrivateRoute>} />
        {/* Работник: только просмотр своего проекта (read-only, без смет/контроля — фильтруется на бэке) */}
        <Route path="projects/:id" element={<PrivateRoute perm="projects.view"><ProjectDetailPage /></PrivateRoute>} />
        {/* Реестр Смет. moduleFlag не нужен: quotes — модуль реестра прав, и
            тумблер quotes_enabled бэкенд уже снял в снимке прав (MODULE_ORG_TOGGLE). */}
        <Route path="quotes" element={<PrivateRoute perm="quotes.view"><QuotesPage /></PrivateRoute>} />
        <Route path="expenses" element={<PrivateRoute perm="expenses.view" moduleFlag="expenses_enabled"><ExpensesPage /></PrivateRoute>} />
        <Route path="requests" element={<PrivateRoute perm="requests.view" moduleFlag="requests_enabled"><RequestsPage /></PrivateRoute>} />
        <Route path="fleet" element={<PrivateRoute perm="equipment.move" moduleFlag="equipment_mobility_enabled"><FleetPage /></PrivateRoute>} />
        <Route path="stats" element={<PrivateRoute perm="dashboard.view" moduleFlag="stats_enabled"><StatsPage /></PrivateRoute>} />
        <Route path="settings" element={<PrivateRoute perm={['org_settings.view', 'equipment.print_tags', ...(SUBSCRIPTION_TAB_ENABLED ? ['subscription.view'] : [])]}><SettingsPage /></PrivateRoute>} />
        <Route path="projects" element={<PrivateRoute perm="projects.view"><ProjectsPage /></PrivateRoute>} />
        {/* Работник: только просмотр своего проекта (read-only, без смет/контроля — фильтруется на бэке) */}
        <Route path="projects/:id" element={<PrivateRoute perm="projects.view"><ProjectDetailPage /></PrivateRoute>} />
        {/* Биллинг тенантов переехал на stat.fixty.io (Founder Dashboard).
            API `/api/billing/orgs` под platformOwnerOnly жив как страховка
            отката — UI в приложении снят. Редирект, а не удаление роута:
            catch-all в приложении нет, и старая закладка (или PWA с закэшенным
            шеллом) молча показала бы пустой экран внутри Layout. */}
        <Route path="billing" element={<Navigate to="/" replace />} />
        <Route path="billing/*" element={<Navigate to="/" replace />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

function KeyboardOffsetProvider() {
  useKeyboardOffset();
  return null;
}

export default function App() {
  // Слушатель beforeinstallprompt — на всё дерево и с самого начала: событие
  // одноразовое, страница Настроек, куда ведёт CTA чек-листа, может открыться
  // намного позже того момента, когда браузер его выстрелил.
  const installPrompt = useInstallPromptState();
  return (
    <BrowserRouter>
      <InstallPromptProvider value={installPrompt}>
      <AuthProvider>
        <KeyboardOffsetProvider />
        <PageviewTracker />
        <AnalyticsConsentBanner />
        <AppRoutes />
        <PushOnboardingPrompt />
        <ServiceWorkerRegistrar />
        <Toaster
          position="top-right"
          containerStyle={{
            top: 'max(16px, env(safe-area-inset-top))',
            right: 'max(16px, env(safe-area-inset-right))',
          }}
          toastOptions={{
            style: {
              background: '#1e2535',
              color: '#e8eaf0',
              border: '1px solid #2a3347',
              fontFamily: "'Golos Text', sans-serif",
              fontSize: '14px',
            },
          }}
        />
      </AuthProvider>
      </InstallPromptProvider>
    </BrowserRouter>
  );
}
